import getPaginatedBlogPosts from '@/api/getPaginatedBlogPosts';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useState } from 'react';
import getBlogPosts from '@/api/getBlogPosts';
import type { BlogQueryName } from '@/utilites/strapi-proxy-queries';

function useFetchPaginatedBlogPosts(locale: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: ['blogs', locale, page, pageSize],
    queryFn: () => getPaginatedBlogPosts(locale, page, pageSize),
  });
}

function useFetchBlogPosts(name: Exclude<BlogQueryName, 'search'>, locale: string) {
  return useQuery({
    queryKey: ['blogs', name, locale],
    queryFn: () => getBlogPosts(name, locale),
  });
}

function useFetchBlogPostsWithQuery() {
  const [textQuery, setTextQuery] = useState<string>('');
  const [query, setQuery] = useState<string | null>(null);
  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null); // Timer para debounce
  const { locale = 'en' } = useRouter();

  const blogsQuery = useQuery({
    queryKey: ['blogs', 'search', query, locale],
    queryFn: () => getBlogPosts('search', locale, query || ''),
    enabled: !!query,
  });

  function handleBlogSearch(searchQuery: string | null) {
    setTextQuery(searchQuery || '');
    setLoadingQuery(true);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(() => {
      setQuery(searchQuery || null);
      setLoadingQuery(false);
    }, 500);

    setDebounceTimer(newTimer);
  }

  return {
    ...blogsQuery,
    isLoading: loadingQuery || blogsQuery.isLoading,
    data: blogsQuery.data || [],
    blogSearch: textQuery,
    handleBlogSearch,
  };
}

export { useFetchBlogPosts, useFetchBlogPostsWithQuery, useFetchPaginatedBlogPosts };
